/**
 * The home page's rows of cards. Without scripts each row scrolls sideways
 * on its own, and Show all is a link that turns the row into a grid. This
 * adds back and forward arrows on wide screens and toggles Show all in
 * place, without jumping the page to the row.
 */
import { caretLeftIcon, caretRightIcon } from "./icons.ts";

const reducedMotion = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function arrow(label: string, icon: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "rail-arrow";
  button.setAttribute("aria-label", label);
  button.innerHTML = icon;
  return button;
}

function setUpRow(section: HTMLElement): void {
  const rail = section.querySelector<HTMLElement>(".rail");
  const head = section.querySelector<HTMLElement>(".sport-head");
  if (!rail || !head) return;
  const name = section.querySelector(".sport-title")?.textContent ?? "";

  const back = arrow(`Scroll ${name} back`, caretLeftIcon);
  const forward = arrow(`Scroll ${name} forward`, caretRightIcon);
  const arrows = document.createElement("div");
  arrows.className = "rail-arrows";
  arrows.append(back, forward);
  head.append(arrows);

  const update = (): void => {
    const end = rail.scrollWidth - rail.clientWidth;
    back.disabled = rail.scrollLeft <= 1;
    forward.disabled = rail.scrollLeft >= end - 1;
    arrows.hidden = end <= 1;
  };
  const step = (direction: 1 | -1): void => {
    const card = rail.querySelector<HTMLElement>(".card");
    const gap = Number.parseFloat(getComputedStyle(rail).columnGap) || 0;
    const cardWidth = (card?.offsetWidth ?? 0) + gap;
    // A screenful of whole cards, so none is skipped half seen.
    const cards = Math.max(1, Math.floor(rail.clientWidth / Math.max(cardWidth, 1)));
    rail.scrollBy({ left: direction * cards * cardWidth, behavior: reducedMotion() ? "instant" : "smooth" });
  };
  back.addEventListener("click", () => {
    step(-1);
  });
  forward.addEventListener("click", () => {
    step(1);
  });
  rail.addEventListener("scroll", update, { passive: true });
  new ResizeObserver(update).observe(rail);
  update();

  // Show all / Show fewer: toggled in place rather than by following the link.
  const showAll = section.querySelector<HTMLAnchorElement>(".show-all");
  const showFewer = section.querySelector<HTMLAnchorElement>(".show-fewer");
  const toggle = (expanded: boolean, focus: HTMLElement | null) => (event: Event) => {
    event.preventDefault();
    if (expanded) section.dataset["expanded"] = "";
    else delete section.dataset["expanded"];
    rail.scrollLeft = 0;
    update();
    focus?.focus();
  };
  showAll?.addEventListener("click", toggle(true, showFewer));
  showFewer?.addEventListener("click", toggle(false, showAll));
}

/** Wires every sport's row on the page. */
export function setUpRails(): void {
  for (const section of document.querySelectorAll<HTMLElement>(".sport")) setUpRow(section);
}
